import { useCallback, useRef } from 'react';

/**
 * Whether the user has changed a piece of form state.
 *
 * A form like the job form fills itself in over several async loads after it
 * mounts, so comparing against its first value would call it changed before
 * anyone touched it. Instead, every write made through `setTracked` - the
 * setter handed to the user's controls - records the value as it stood just
 * before the first such write, and the form is dirty while it differs from
 * that. Loads keep using the plain setter and are never counted.
 */
export function useTrackedEdits(value, setValue) {
  const baseline = useRef(null);

  const setTracked = useCallback(
    (update) =>
      setValue((prev) => {
        if (baseline.current === null) baseline.current = JSON.stringify(prev);
        return typeof update === 'function' ? update(prev) : update;
      }),
    [setValue],
  );

  const dirty = baseline.current !== null && JSON.stringify(value) !== baseline.current;
  return { dirty, setTracked };
}
